import React, { useState, useRef, useEffect, useCallback, memo } from 'react'
import Box from '@/primitives/Box'
import FlexV2 from '@/primitives/FlexV2'
import Icon from '@/primitives/Icon'
import Text from '@/primitives/Text'
import Button from '@/primitives/Button'
import { SectionHeader, AnimatedMessage } from './shared'
import { getAudioPath } from '../constants'
import Strings from '@/components/TrialModeView/Strings'
import { getLang } from '@/reducers/selectors'

const AudioOverview = memo(() => {
  const [generating, setGenerating] = useState(false)
  const [audioReady, setAudioReady] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [error, setError] = useState(null)

  const audioRef = useRef(null)
  const progressRef = useRef(null)
  const strings = Strings()
  const lang = getLang()

  const AUDIO_PATH = getAudioPath(lang)

  const formatTime = useCallback(timeInSeconds => {
    if (isNaN(timeInSeconds)) return '0:00'
    const minutes = Math.floor(timeInSeconds / 60)
    const seconds = Math.floor(timeInSeconds % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }, [])

  const updateProgressBar = useCallback(() => {
    if (audioRef.current && progressRef.current && audioRef.current.duration) {
      const percent =
        (audioRef.current.currentTime / audioRef.current.duration) * 100
      progressRef.current.style.width = `${percent}%`
    }
  }, [])

  const handleLoadedData = useCallback(() => {
    if (
      audioRef.current &&
      audioRef.current.duration &&
      !isNaN(audioRef.current.duration)
    ) {
      setDuration(audioRef.current.duration)
    }
  }, [])

  const handleDurationChange = useCallback(() => {
    if (
      audioRef.current &&
      audioRef.current.duration &&
      !isNaN(audioRef.current.duration)
    ) {
      setDuration(audioRef.current.duration)
    }
  }, [])

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
      updateProgressBar()
    }
  }, [updateProgressBar])

  const handleEnded = useCallback(() => {
    setIsPlaying(false)
    updateProgressBar()
  }, [updateProgressBar])

  const handleAudioError = useCallback(e => {
    setError(strings.failedToLoadAudio)
    setGenerating(false)
    setAudioReady(false)
  }, [])

  const setupAudioListeners = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.addEventListener('loadeddata', handleLoadedData)
      audioRef.current.addEventListener('durationchange', handleDurationChange)
      audioRef.current.addEventListener('timeupdate', handleTimeUpdate)
      audioRef.current.addEventListener('ended', handleEnded)
      audioRef.current.addEventListener('error', handleAudioError)
    }
  }, [
    handleLoadedData,
    handleDurationChange,
    handleTimeUpdate,
    handleEnded,
    handleAudioError
  ])

  const cleanupAudioListeners = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.removeEventListener('loadeddata', handleLoadedData)
      audioRef.current.removeEventListener(
        'durationchange',
        handleDurationChange
      )
      audioRef.current.removeEventListener('timeupdate', handleTimeUpdate)
      audioRef.current.removeEventListener('ended', handleEnded)
      audioRef.current.removeEventListener('error', handleAudioError)
    }
  }, [
    handleLoadedData,
    handleDurationChange,
    handleTimeUpdate,
    handleEnded,
    handleAudioError
  ])

  // Set up and clean up audio handling
  useEffect(() => {
    // Initialize audio element if it's ready
    if (audioReady && audioRef.current) {
      setupAudioListeners()

      // Return cleanup function
      return () => {
        cleanupAudioListeners()
        if (audioRef.current) {
          audioRef.current.pause()
          audioRef.current.src = '' // Clear source to cancel any pending operations
        }
      }
    }

    // Clean up only without initialization
    return () => {
      cleanupAudioListeners()
    }
  }, [audioReady, setupAudioListeners, cleanupAudioListeners])

  const handleGenerateAudio = useCallback(() => {
    // Reset current state
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = '' // Clear source to cancel any pending operations
      cleanupAudioListeners() // Remove existing listeners to prevent duplicates
      setIsPlaying(false)
    }

    setGenerating(true)
    setError(null)
    setAudioReady(false)
    setCurrentTime(0)
    setDuration(0)

    // Store timer references
    let generationTimer
    let durationCheckTimer

    // Simulate generation delay
    generationTimer = setTimeout(() => {
      setGenerating(false)
      setAudioReady(true)

      // Initialize audio only after generation is complete
      if (audioRef.current) {
        // Set source and prepare listeners
        audioRef.current.src = AUDIO_PATH
        setupAudioListeners()

        // Explicitly load and set up duration
        audioRef.current.load()

        // Set a timeout to handle cases where loadeddata or durationchange events don't fire
        durationCheckTimer = setTimeout(() => {
          if (audioRef.current && (!duration || isNaN(duration))) {
            const tempAudio = new Audio(AUDIO_PATH)
            const onMetadata = () => {
              if (tempAudio.duration && !isNaN(tempAudio.duration)) {
                setDuration(tempAudio.duration)
                tempAudio.removeEventListener('loadedmetadata', onMetadata)
                tempAudio.removeEventListener('error', onError)
              }
            }
            const onError = () => {
              handleAudioError(new Error('Failed to determine audio duration'))
              tempAudio.removeEventListener('loadedmetadata', onMetadata)
              tempAudio.removeEventListener('error', onError)
            }

            tempAudio.addEventListener('loadedmetadata', onMetadata)
            tempAudio.addEventListener('error', onError)
            tempAudio.load()
          }
        }, 1000)
      }
    }, 3500)

    // Return a proper cleanup function from the main component
    return () => {
      clearTimeout(generationTimer)
      clearTimeout(durationCheckTimer)
    }
  }, [handleAudioError, setupAudioListeners, cleanupAudioListeners, duration])

  const togglePlayPause = useCallback(() => {
    if (audioRef.current) {
      try {
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          const playPromise = audioRef.current.play()
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              setError(strings.playbackFailed)
            })
          }
        }
        setIsPlaying(!isPlaying)
      } catch (err) {
        setError(strings.playbackControlFailed)
      }
    }
  }, [isPlaying])

  const handleProgressClick = useCallback(e => {
    if (
      audioRef.current &&
      progressRef.current &&
      progressRef.current.parentElement
    ) {
      try {
        const progressBar = progressRef.current.parentElement
        const clickPosition =
          (e.clientX - progressBar.getBoundingClientRect().left) /
          progressBar.offsetWidth
        audioRef.current.currentTime = clickPosition * audioRef.current.duration
      } catch (err) {
        setError(strings.failedToUpdatePlayback)
      }
    }
  }, [])

  const handleRetry = useCallback(() => {
    // Reset all states
    setError(null)
    setAudioReady(false)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)

    // Clean up audio element
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.src = ''
      cleanupAudioListeners()
    }
  }, [cleanupAudioListeners])

  return (
    <Box className='audio-overview'>
      <FlexV2 direction='column' axisGap={200}>
        <SectionHeader icon='volume_up' title={strings.audioOverview} />

        {!audioReady && !generating && !error && (
          <Button
            variant='primary'
            iconBefore='mic'
            onClick={handleGenerateAudio}
          >
            {strings.generateAudioOverview}
          </Button>
        )}

        {generating && (
          <AnimatedMessage
            message={strings.processingVoiceSynthesis}
            status='processing'
          />
        )}

        {error && (
          <Box className='error-message'>
            <FlexV2 direction='column' axisGap={100}>
              <FlexV2 alignCrossAxis='center' axisGap={100}>
                <Icon name='error' size={200} variant='danger' />
                <Text color='danger'>{error}</Text>
              </FlexV2>
              <Button
                variant='outline'
                onClick={handleRetry}
                iconBefore='refresh'
              >
                {strings.tryAgain}
              </Button>
            </FlexV2>
          </Box>
        )}

        {audioReady && !error && (
          <>
            <Box className='audio-player'>
              <audio
                ref={audioRef}
                src={AUDIO_PATH}
                preload='metadata'
                onLoadedMetadata={e => {
                  if (e.target.duration && !isNaN(e.target.duration)) {
                    setDuration(e.target.duration)
                    setTimeout(updateProgressBar, 100)
                  }
                }}
              />

              <FlexV2 alignCrossAxis='center' alignMainAxis='space-between'>
                <Box className='play-button' onClick={togglePlayPause}>
                  <Icon
                    name={isPlaying ? 'pause_circle' : 'play_circle'}
                    size={300}
                    variant='primary'
                  />
                </Box>

                <Box className='audio-progress' onClick={handleProgressClick}>
                  <Box
                    className='progress-bar'
                    ref={progressRef}
                    style={{
                      width: `${(currentTime / (duration || 1)) * 100}%`
                    }}
                  ></Box>
                </Box>

                <Box className='time-display'>
                  <Text>
                    {formatTime(currentTime)} /{' '}
                    {isNaN(duration) ? '--:--' : formatTime(duration)}
                  </Text>
                </Box>
              </FlexV2>
            </Box>

            <Button
              variant='outline'
              iconBefore='refresh'
              onClick={handleGenerateAudio}
              className='regenerate-button'
            >
              {strings.regenerateAudio}
            </Button>
          </>
        )}
      </FlexV2>
    </Box>
  )
})

export default AudioOverview
